import { useState } from 'react';
import {
  NudosButton,
  NudosStaticAlertInComponent,
  NudosTextInput
} from '../../../../../../../../components/NudosComponents';
import NudosDatePicker from '../../../../../../../../components/NudosComponents/NudosDatePicker';
import {
  IUpdateTemporaryStorage,
  TEMPORARY_STORAGE_REASON_TYPE_ID,
  IWarehouseTemporaryStorageReason,
  IShipmentData,
  IMinDetails,
  IproductReferenceDataInCountry,
  IBulkTemporaryStorageRequest,
  SHIPMENT_STATUS
} from '../../../../../../../../types/orders';
import {
  createBulkTemporaryStorageForShipments,
  createTemporaryStorageForShipment,
  updateTemporaryStorageForShipment
} from '../../../../../../../../services/orders';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../../utils/displayNudosStandardNotifications';
import ProductSelector from 'views/orders/Components/ProductSelector/ProductSelector';
import { isValidUrl } from 'utils/validateFiles';

export const WarehouseTemporaryStorageModalCustomSection = ({
  shipmentId,
  reasonsForTemporaryStorage,
  initialValues,
  closeModalCallback,
  reloadDataCallback,
  organizationName = 'Nombre Empresa',
  orderId,
  flag = null,
  shipmentData,
  receiverName,
  references,
  listOfShipmentsAvailableToAdd,
  listOfSelectedShipments
}: IWarehouseTemporaryStorageModalCustomSectionProps) => {
  const [estimatedEntryDate, setEstimatedEntryDate] = useState<string | undefined>(initialValues.estimatedEntryDate);
  const [trackingCode, setTrackingCode] = useState<string | undefined>(initialValues.trackingCode);
  const [trackingUrl, setTrackingUrl] = useState<string | undefined>(initialValues.trackingUrl);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listOfShipmentsToChangeStatus, setListOfShipmentsToChangeStatus] = useState<IShipmentData[]>([]);
  const [errorList, setErrorList] = useState<IMinDetails[] | undefined>([]);
  const [hasProductSelectorErrors, setHasProductSelectorErrors] = useState<boolean>(false);
  const isBulkAction = listOfSelectedShipments?.length && listOfSelectedShipments?.length > 0;

  const defaultFlag =
    'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/Flag_of_Mexico.svg/2560px-Flag_of_Mexico.svg.png';

  const addedReasons = reasonsForTemporaryStorage.filter(
    reason => !initialValues.warehouseTemporaryStorageReasons?.some(r => r.temporaryStorageReasonId === reason)
  );
  const removedReasons = initialValues.warehouseTemporaryStorageReasons?.filter(
    reason => !reasonsForTemporaryStorage.some(r => r === reason.temporaryStorageReasonId)
  );

  const reasonsHaveChanged =
    initialValues.warehouseTemporaryStorageReasons?.length && (!!addedReasons.length || !!removedReasons?.length);

  const dataHasChanged =
    estimatedEntryDate !== initialValues.estimatedEntryDate ||
    trackingCode !== initialValues.trackingCode ||
    trackingUrl !== initialValues.trackingUrl ||
    reasonsHaveChanged;

  const validateTrackingUrl = (trackingUrl?: string) => {
    if (!trackingUrl?.trim()) return true;
    return isValidUrl(trackingUrl);
  };

  const isButtonDisabled =
    !estimatedEntryDate ||
    !dataHasChanged ||
    hasProductSelectorErrors ||
    !listOfShipmentsToChangeStatus?.length ||
    !validateTrackingUrl(trackingUrl);

  const getSerialsToUpdate = (listOfShipmentsToChangeStatus: IShipmentData[]) => {
    return listOfShipmentsToChangeStatus?.flatMap(shipment =>
      shipment?.products?.map(({ id, serial, saleId }) => ({
        productId: id,
        serial,
        saleId
      }))
    );
  };

  const handleContinue = async () => {
    if (!shipmentId || !estimatedEntryDate || isLoading) return;
    setIsLoading(true);
    try {
      const singleWarehouseTemporaryStorageBody: IUpdateTemporaryStorage = {
        shipmentId,
        reasons: reasonsForTemporaryStorage,
        estimatedEntryDate,
        trackingCode: trackingCode || undefined,
        trackingUrl: trackingUrl?.trim() || undefined,
        newSerials: getSerialsToUpdate(listOfShipmentsToChangeStatus)
      };

      const bulkWarehouseTemporaryStorageBody: IBulkTemporaryStorageRequest = {
        shipmentIds: listOfShipmentsToChangeStatus?.map(shipment => shipment.shipmentId) || [],
        reasons: reasonsForTemporaryStorage,
        estimatedEntryDate,
        trackingCode: trackingCode || undefined,
        trackingUrl: trackingUrl?.trim() || undefined,
        serialsByShipment:
          listOfShipmentsToChangeStatus?.map(shipment => ({
            shipmentId: shipment.shipmentId,
            newSerials: shipment.products.map(product => ({
              productId: product.id,
              serial: product.serial || '',
              saleId: product.saleId
            }))
          })) || []
      };

      const isUpdate = !!initialValues.warehouseTemporaryStorageReasons?.length;

      const successsMessage = isUpdate
        ? 'Parada en bodega actualizada correctamente'
        : 'Parada en bodega creada correctamente';

      if (isBulkAction) {
        await createBulkTemporaryStorageForShipments(bulkWarehouseTemporaryStorageBody);
      } else if (isUpdate) {
        await updateTemporaryStorageForShipment(singleWarehouseTemporaryStorageBody);
      } else {
        await createTemporaryStorageForShipment(singleWarehouseTemporaryStorageBody);
      }
      await reloadDataCallback?.();
      closeModalCallback();
      displaySuccessNotification({ customJSXMessage: <div>{successsMessage}</div>, duration: 10000 });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const errorTrackingUrl = !validateTrackingUrl(trackingUrl?.trim()) ? 'El link de tracking interno no es válido' : '';

  return (
    <div className="warehouseTemporaryStorageModalCustomSection">
      <div className="containerSubTitle">
        <span className="firstPartSubTitle">{`${organizationName} - Orden  N°${orderId ?? 1}`}</span>
        <span>|</span>
        <span>Envío - {receiverName}</span>
        <img src={flag ?? defaultFlag} width={14} alt="warehouse-temporary-storage-flag" />
      </div>
      <div className="contents">
        <NudosDatePicker
          componentSize="small"
          label="Fecha estimada de llegada a bodega *"
          isFilled={!!estimatedEntryDate}
          customClassName="customInput"
          defaultValue={initialValues.estimatedEntryDate || ''}
          updateCurrentValueCallback={setEstimatedEntryDate}
        />
        <NudosTextInput
          label="Código/guía de rastreo"
          componentSize="small"
          placeholder="Escribe el código de rastreo"
          isFilled={!!trackingCode}
          handleChange={setTrackingCode}
          defaultValue={initialValues.trackingCode || ''}
        />
        <NudosTextInput
          label="Link de tracking interno"
          componentSize="large"
          placeholder="Pega el link de tracking interno"
          isFilled={!!trackingUrl}
          handleChange={setTrackingUrl}
          defaultValue={initialValues.trackingUrl || ''}
          errorText={errorTrackingUrl}
        />
      </div>
      <NudosStaticAlertInComponent
        type="information"
        alertOptions={{
          title: 'La fecha y el seguimiento son de uso interno; el cliente no verá esta información.'
        }}
      />
      <ProductSelector
        headerTitle="Selecciona las herramientas que pasan a tener parada en bodega"
        shippingChosenByDefault={shipmentData}
        listOfShipmentsAvailableToAdd={listOfShipmentsAvailableToAdd?.filter(
          shipment => shipment.status === SHIPMENT_STATUS.CONFIRMED
        )}
        listOfSelectedShipments={listOfSelectedShipments}
        setListOfShipmentsToChangeStatus={setListOfShipmentsToChangeStatus}
        hasTemporaryStorage
        errorList={errorList}
        setErrorList={setErrorList}
        serialHasAnyError={hasErrors => setHasProductSelectorErrors(hasErrors)}
        references={references}
      />

      <div className="buttons">
        <NudosButton
          componentSize="large"
          colorPalette="primary"
          buttonText="Guardar"
          handleClick={handleContinue}
          isButtonDisabled={isButtonDisabled}
          isButtonLoading={isLoading}
        />
      </div>
    </div>
  );
};

interface IWarehouseTemporaryStorageModalCustomSectionProps {
  shipmentId: number;
  reasonsForTemporaryStorage: TEMPORARY_STORAGE_REASON_TYPE_ID[];
  initialValues: IWarehouseTemporaryStorageModalCustomSectionInitialValues;
  closeModalCallback: () => void;
  reloadDataCallback?: () => Promise<void>;
  organizationName?: string;
  orderId?: string;
  flag?: string | null;
  shipmentData?: IShipmentData;
  receiverName?: string;
  references?: IproductReferenceDataInCountry[];
  listOfShipmentsAvailableToAdd?: IShipmentData[];
  listOfSelectedShipments?: IShipmentData[];
}

export interface IWarehouseTemporaryStorageModalCustomSectionInitialValues {
  estimatedEntryDate?: string;
  trackingCode?: string;
  trackingUrl?: string;
  warehouseTemporaryStorageReasons?: IWarehouseTemporaryStorageReason[];
}
